import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, useTheme, Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { TableToolbarProps } from './playerTable.interface';
import { TextInput } from '../../../components/textInput/textInput';
import { SelectInput } from '../../../components/select/selectInput';
import { Button } from '../../../components/button/button';
import { AddPlayerButton } from '../addPlayerModal/addPlayerButton';
import { AddConneqtModal } from '../addConneqtModal/addConneqtModal';
import { AccountTypes } from '../../../components/changeUser/accountTypes.enum';
import { AlertDialog } from '../../../components/alertDialog/alertDialog';
import { AddPlayer } from '../addPlayerModal/addPlayer';
import { BulkUploader } from '../../../components/upload/bulkUploader';
import {
  bulkUploadPlayerDataMapper,
  bulkUploadPlayerUploader,
  bulkUploadPlayerValidator,
  templateFile,
} from '../bulkUploaderPlayer';
import { PlayerDataTableList } from '../../../state/reduxRepositories/players/player.interfaces';

export function PlayerTableToolbar({
  nameFilter,
  setNameFilter,
  onRequestSort,
  dispatchCreatePlayer,
  accountType,
  onDownloadPlayerTemplate,
  hidePlayerListActions,
  dispatchDownloadPlayerTemplate,
  showSaveButton,
  onSavePlayer,
  isSavingPlayer,
  uploadDisabled,
}: TableToolbarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(
    location.state?.addPlayerModal || false,
  );
  const [showUploadCSVModal, setShowUploadCSVModal] = useState(false);

  const sortByItems: { value: keyof PlayerDataTableList; text: string }[] = [
    {
      value: 'playerName',
      text: t('NAME'),
    },
    {
      value: 'clearStatus',
      text: t('CLEAR_STATUS'),
    },
    {
      value: 'dob',
      text: t('DOB'),
    },
    {
      value: 'conneqtedStatus',
      text: t('CONNEQTED_STATUS'),
    },
  ];

  useEffect(() => {
    if (showUploadCSVModal) {
      dispatchDownloadPlayerTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUploadCSVModal]);

  return (
    <Toolbar>
      <Grid
        container
        spacing={1}
        direction="row"
        justifyContent="center"
        alignItems="center"
      >
        <Grid item xs={12} sm={12} md={2} lg={2}>
          <TextInput
            testId="playerName"
            name="playerName"
            label={t('NAME').toString()}
            type="textInput"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={5} lg={5}>
          <Box sx={{ maxWidth: '200px', pt: 1 }}>
            <SelectInput
              fullWidth
              label={t('SORT_BY').toString()}
              aria-label="select-filter"
              name="player-select"
              items={sortByItems}
              onChange={(e) => {
                const selectedSortItem = sortByItems.find(
                  (item) => item.value === e.target.value,
                );
                if (selectedSortItem) {
                  onRequestSort(selectedSortItem.value);
                }
              }}
            />
          </Box>
        </Grid>
        {!hidePlayerListActions && (
          <Grid item xs={12} sm={12} md={5} lg={5}>
            <Grid
              container
              spacing={1}
              display="flex"
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Grid item sm={6} xs={6} md={6} lg={6}>
                {accountType === AccountTypes.Org && (
                  <Button
                    startIcon={<UploadFileIcon />}
                    size="medium"
                    label={t('bulkupload.UPLOAD')}
                    variant="outlined"
                    onClick={() => setShowUploadCSVModal(true)}
                    type="button"
                    disabled={uploadDisabled}
                  />
                )}
              </Grid>
              <Grid
                item
                sm={accountType === AccountTypes.Clinic ? 12 : 6}
                xs={accountType === AccountTypes.Clinic ? 12 : 6}
                md={6}
                lg={6}
              >
                <AddPlayerButton
                  size="medium"
                  onPress={() => {
                    setShowAddPlayerModal(true);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        {showSaveButton && (
          <Grid
            item
            xs={12}
            sm={12}
            md={5}
            lg={5}
            display="flex"
            justifyContent={isSmall || isMedium ? 'flex-start' : 'flex-end'}
            alignItems={isSmall || isMedium ? 'flex-start' : 'flex-end'}
          >
            {accountType === AccountTypes.Org && (
              <Button
                sx={{ mb: 1.5, mt: 1.5 }}
                loading={isSavingPlayer}
                loadingPosition="start"
                startIcon={<SaveAltIcon />}
                size="large"
                label={t('SAVE_TEAM_PLAYER').toString()}
                variant="contained"
                onClick={onSavePlayer}
                type="button"
                color="primary"
                testId="updateTeamPlayers"
              />
            )}
          </Grid>
        )}
      </Grid>
      <AlertDialog
        title={
          accountType === AccountTypes.Clinic
            ? t('ADD_PATIENT').toString()
            : t('ADD_PLAYER').toString()
        }
        setOpen={setShowAddPlayerModal}
        open={showAddPlayerModal}
        content={
          <AddPlayer
            dispatchCreatePlayer={dispatchCreatePlayer}
            closeModal={setShowAddPlayerModal}
          />
        }
      />
      <AddConneqtModal />
      <AlertDialog
        title={
          <Grid container spacing={1}>
            <Grid item xs={4} sm={4} md={4} lg={4} sx={{ mt: 1 }}>
              <Typography>{t('UPLOAD_CSV').toString()}</Typography>
            </Grid>
            <Grid
              item
              xs={8}
              sm={8}
              md={8}
              lg={8}
              display="flex"
              justifyContent="flex-end"
              sx={{ mt: 1 }}
            >
              <Button
                onClick={onDownloadPlayerTemplate}
                label={t('DOWNLOAD_UPLOAD_PLAYER_TEMPLATE').toString()}
                variant="text"
                type="button"
              />
            </Grid>
          </Grid>
        }
        setOpen={setShowUploadCSVModal}
        open={showUploadCSVModal}
        content={
          <>
            {accountType === AccountTypes.Org && (
              <BulkUploader
                setShowUploadCSVModal={setShowUploadCSVModal}
                dataMapper={bulkUploadPlayerDataMapper}
                validator={bulkUploadPlayerValidator}
                uploader={bulkUploadPlayerUploader}
                templateFile={templateFile}
                onCancel={() => {
                  setShowUploadCSVModal(false);
                }}
              />
            )}
          </>
        }
      />
    </Toolbar>
  );
}
